import { useState, useEffect, useCallback } from 'react';
import { ISelectOptionProps } from 'components/form/types';

/**
 * Apply saved filter values to dropdown options by marking them as selected
 * @param options - The dropdown options array
 * @param filterName - The name of the filter
 * @param savedFilters - The saved filter values object
 * @returns Options array with selected state applied
 */
export const applyFiltersToOptions = (
  options: ISelectOptionProps[] | undefined,
  filterName: string,
  savedFilters: Record<string, any> | null
): ISelectOptionProps[] | undefined => {
  if (!options || !savedFilters || !savedFilters[filterName]) return options;
  const selectedValues = savedFilters[filterName];

  return options.map(opt => {
    let isSelected = false;

    if (Array.isArray(selectedValues)) {
      isSelected = selectedValues.some(val => {
        if (typeof opt.value === 'number') {
          return Number(val) === opt.value;
        }
        return String(val) === String(opt.value);
      });
    } else if (typeof opt.value === 'number') {
      isSelected = Number(selectedValues) === opt.value;
    } else {
      isSelected = String(selectedValues) === String(opt.value);
    }

    return {
      ...opt,
      selected: isSelected,
    };
  });
};

/**
 * Custom hook for persisting table filters in sessionStorage
 * @param storageKey - The sessionStorage key to use for persistence
 * @param dropdowns - The dropdown options data
 * @returns Object containing initialFilters and handleFiltersChange callback
 */
export const useFilterPersistence = (
  storageKey: string,
  dropdowns: { [key: string]: ISelectOptionProps[] } | undefined
) => {
  const [initialFilters, setInitialFilters] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    if (dropdowns && !initialFilters) {
      const savedFilters = sessionStorage.getItem(storageKey);
      if (savedFilters) {
        try {
          const parsedFilters = JSON.parse(savedFilters);
          setInitialFilters(parsedFilters);
        } catch (error) {
          console.error('Failed to parse saved filters:', error);
        }
      }
    }
  }, [dropdowns, initialFilters, storageKey]);

  const handleFiltersChange = useCallback(
    (filters: any) => {
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    },
    [storageKey]
  );

  return { initialFilters, handleFiltersChange };
};
